import { useCallback, useEffect, useRef, useState } from 'react';
import copy from 'copy-to-clipboard';
import * as microsoftTeams from '@microsoft/teams-js';
import Lottie from 'lottie-react';
import LoadingAnimation from './assets/loading.json';
import WordLogo from '~/assets/word.svg';
import OutlookLogo from '~/assets/outlook.svg';
import TeamsLogo from '~/assets/teams.svg';
import ChromeLogo from '~/assets/chrome.svg';
import GmailLogo from '~/assets/gmail.svg';
import TelegramLogo from '~/assets/telegram.svg';
import PowerPointLogo from '~/assets/powerpoint.svg';
import ExcelLogo from '~/assets/excel.svg';
import DocsLogo from '~/assets/docs.svg';
import SheetsLogo from '~/assets/sheets.svg';
import SlidesLogo from '~/assets/slides.svg';

import {
    CopyOutlined,
    ShareAltOutlined,
    InfoCircleOutlined,
    SendOutlined,
    BellOutlined,
} from '@ant-design/icons';
import type { FormInstance } from 'antd';
import {
    Space,
    Select,
    Button,
    Form,
    Input,
    Row,
    Col,
    Drawer,
    Segmented,
    Modal,
    Tag,
    Switch,
    Upload,
    Dropdown,
} from 'antd';
import { useSearchParams } from '@remix-run/react';
import { useDialog, useDisplayTokens, useMessagesAPI } from '~/hooks';
import { atom, useAtom } from 'jotai';
import type { PromptDefinition } from '~/services';
import { usePostHog } from '~/services/postHog-client';
import { useApplicationContext } from '../ApplicationContext';
import { useSubscriptionPromotionModal } from '../SubscriptionPromotionModal';
import { useSignInModal } from '../SiginModal';
import { useDebouncedCallback } from 'use-debounce';

import { atomWithStorage } from 'jotai/utils';
import AiButton from '../AiButton';
import { getLanguagesList } from '~/services/languages';
import { Copy, Image } from 'lucide-react';

import type { UploadFile, UploadProps } from 'antd';
import ImgCrop from 'antd-img-crop';
import dayjs from 'dayjs';
import { downloadDocx } from '../ToDocx';

const DEFAULT_FORM_VALUES = {
    tone: 'professional',
    language: 'english',
    length: 'short',
    text: null,
};
export const defaultFormValues = atomWithStorage(
    'formValues',
    DEFAULT_FORM_VALUES,
    undefined,
    {
        getOnInit: true,
    },
);

function UserInputForm({
    aiTemplate,
    form,
    isFreeMode = false,
    handleShowIntegrationsClick,
}: {
    aiTemplate: PromptDefinition;
    form: FormInstance<any>;
    isFreeMode?: boolean;
    handleShowIntegrationsClick: () => void;
}) {
    const CHARS_LIMIT = 10000;

    const label = (
        <div className='w-full flex items-center'>
            <span className='text-xs'>{aiTemplate.textLabel}</span>
            <Button
                size='small'
                type='link'
                onClick={() => form.setFieldValue('text', aiTemplate.example)}>
                <span className='text-xs'>Show example prompt</span>
            </Button>
        </div>
    );

    function UseAdvancedAiModelPopup() {
        const [isModalOpen, setIsModalOpen] = useState(false);
        const showModal = () => {
            setIsModalOpen(true);
        };
        const handleOk = () => {
            setIsModalOpen(false);
        };
        const handleCancel = () => {
            setIsModalOpen(false);
        };
        return (
            <>
                <div className='mt-4 flex items-center gap-2'>
                    <Switch value={false} onClick={showModal} />
                    <span>Use Advanced AI Model?</span>
                    <div className='hidden sm:block'>
                        <Tag color='green'>✨ Best Results ✨</Tag>
                    </div>
                </div>
                <Modal
                    title='Get Smarter Answers—For Free!'
                    open={isModalOpen}
                    onOk={handleOk}
                    onCancel={handleCancel}
                    footer={null}>
                    <p>
                        Our most advanced AI is ready to help you. Sign up in
                        seconds and start using it for free—no strings attached!
                    </p>
                    <div className='py-4'>
                        <Button
                            size='large'
                            type='primary'
                            className='w-full'
                            href='/iframe/signin?source=better_ai_model'>
                            Sign Up&nbsp;&rarr;
                        </Button>
                    </div>
                </Modal>
            </>
        );
    }

    function GetAddInsPromoButton() {
        function getRandomIndex(arr: any[]): number {
            return Math.floor(Math.random() * arr.length);
        }

        const options = [
            { logoSrc: OutlookLogo, label: 'Get Outlook Add-in' },
            { logoSrc: WordLogo, label: 'Get Word Add-in' },
            { logoSrc: TeamsLogo, label: 'Get Teams Add-in' },
            { logoSrc: ExcelLogo, label: 'Get Excel Add-in' },
            { logoSrc: SlidesLogo, label: 'Get Slides Add-in' },
            { logoSrc: DocsLogo, label: 'Get Docs Add-in' },
            { logoSrc: SheetsLogo, label: 'Get Sheets Add-in' },
            { logoSrc: ChromeLogo, label: 'Get Chrome Extension' },
            { logoSrc: GmailLogo, label: 'Get Gmail Add-in' },
            { logoSrc: TelegramLogo, label: 'Get Telegram Bot' },
            { logoSrc: PowerPointLogo, label: 'Get PowerPoint Add-in' },
            {
                imgEl: <BellOutlined rotate={45} />,
                label: "What's new?",
                url: '/whats-new',
            },
        ];

        const rndIdx = getRandomIndex(options);
        const rndOption = options[rndIdx];
        const handleClick = () => {
            if (rndOption.url) {
                window.open(rndOption.url, '_blank');
            } else {
                handleShowIntegrationsClick();
            }
        };

        return (
            <div className='mt-4 flex gap-2'>
                <Button onClick={handleClick}>
                    {rndOption.imgEl || (
                        <img
                            alt='Product logo'
                            src={rndOption.logoSrc}
                            className='w-8 h-8'
                        />
                    )}
                    {rndOption.label}&nbsp;&rarr;
                </Button>
            </div>
        );
    }

    function UploadAndCrop() {
        const [fileList, setFileList] = useState<UploadFile[]>([]);

        const onChange: UploadProps['onChange'] = ({
            fileList: newFileList,
        }) => {
            setFileList(newFileList.filter((f) => f.type?.includes('image/')));
        };

        const uploadImage = async (options: any) => {
            const { onSuccess, onError, file, onProgress, category } = options;

            const fmData = new FormData();
            fmData.append('file', file);
            try {
                const res = await fetch(`/file/upload`, {
                    method: 'POST',
                    body: fmData,
                });
                onSuccess('ok', res);
                const imgUrl = await res.text();
                form.setFieldValue('imgUrl', imgUrl);
            } catch (err) {
                console.log('Error while uploading file: ', err);
                onError({ err });
            }
        };

        return (
            <ImgCrop
                rotationSlider
                aspectSlider
                maxAspect={10}
                beforeCrop={(file) => {
                    if (!file.type.includes('image/')) {
                        return false;
                    }
                }}>
                <Upload
                    accept='image/*'
                    name={'file'}
                    customRequest={uploadImage}
                    pastable={true}
                    listType='picture-card'
                    fileList={fileList}
                    onChange={onChange}
                    maxCount={1}
                    showUploadList={{
                        showDownloadIcon: false,
                        showPreviewIcon: false,
                    }}>
                    {fileList.length < 1 && (
                        <div className='w-full h-full flex items-center justify-center text-gray-500'>
                            <Image className='mr-2' /> Click to Upload or Paste
                            File
                        </div>
                    )}
                </Upload>
            </ImgCrop>
        );
    }

    const isOCRPrompt = aiTemplate.id === 'ocr-text';

    if (isOCRPrompt) {
        return (
            <div className='h-full flex flex-col'>
                <div className='mx-4 flex-grow flex flex-col'>
                    <Form.Item label='Paste or Upload image to recognize text from'>
                        <Form.Item
                            name='imgUrl'
                            noStyle
                            rules={[
                                {
                                    required: true,
                                    message: 'Please upload image to process',
                                },
                            ]}>
                            <UploadAndCrop />
                        </Form.Item>
                    </Form.Item>
                </div>
                <div className='bg-slate-50 border-t flex flex-col sticky bottom-0 w-full items-center justify-center'>
                    {isFreeMode ? (
                        <UseAdvancedAiModelPopup />
                    ) : (
                        <GetAddInsPromoButton />
                    )}
                    <AiButton>Process with AI</AiButton>
                </div>
            </div>
        );
    }

    return (
        <div className='h-full flex flex-col'>
            <div className='mx-4 flex-grow flex flex-col'>
                {aiTemplate.showToneSelector && (
                    <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                        <Col className='gutter-row' xs={24} sm={8}>
                            <Form.Item
                                key={`item-tone`}
                                label='Tone'
                                className='!mb-2'
                                name='tone'>
                                <Select
                                    className='flex-grow'
                                    options={[
                                        {
                                            value: 'professional',
                                            label: '💼 Professional',
                                        },
                                        { value: 'casual', label: '🙂 Casual' },
                                        { value: 'urgent', label: '⏰ Urgent' },
                                        {
                                            value: 'appreciative',
                                            label: '💕 Appreciative',
                                        },
                                        { value: 'funny', label: '😄 Funny' },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                        <Col className='gutter-row' xs={24} sm={8}>
                            <Form.Item
                                key={`item-language`}
                                className='!mb-2'
                                label='Language'
                                name='language'>
                                <Select
                                    showSearch
                                    options={getLanguagesList()}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                )}
                <Form.Item
                    className='flex-grow'
                    key={`item-text`}
                    name={'text'}
                    label={label}
                    rules={[
                        {
                            required: true,
                            message: 'Please input something',
                        },
                        {
                            max: CHARS_LIMIT,
                            message: `Max length is ${CHARS_LIMIT}`,
                        },
                    ]}>
                    <Input.TextArea
                        style={{ resize: 'none', height: '250px', zIndex: 0 }}
                        showCount={{
                            formatter: ({ value, count, maxLength }) => {
                                const diff = CHARS_LIMIT - count;
                                if (diff >= 0) {
                                    return `${diff} characters left`;
                                }

                                return `Over limit by ${diff * -1} characters`;
                            },
                        }}
                        placeholder={aiTemplate.textPlaceholder}
                    />
                </Form.Item>
            </div>
            <div className='bg-slate-50 border-t flex flex-col sticky bottom-0 w-full items-center justify-center'>
                {isFreeMode ? (
                    <UseAdvancedAiModelPopup />
                ) : (
                    <GetAddInsPromoButton />
                )}
                <AiButton>Generate with AI</AiButton>
            </div>
        </div>
    );
}

function SubmitToComposeBoxButton({ result }: { result: string }) {
    async function handleSubmitToComposeBoxButtonClick() {
        const content = result;

        await microsoftTeams.app.initialize();
        microsoftTeams.dialog.url.submit(
            `${content} (⚠️ It's an AI generated result, it could be incorrect ⚠️)`,
        );
    }

    return (
        <Button
            type='primary'
            icon={<SendOutlined />}
            onClick={handleSubmitToComposeBoxButtonClick}>
            Insert
        </Button>
    );
}

function ShareToTeamsButton({ result }: { result: string }) {
    const [supportSharing, setSupportSharing] = useState(false);
    const postHog = usePostHog();
    useEffect(() => {
        async function initSupportSharing() {
            await microsoftTeams.app.initialize();
            setSupportSharing(microsoftTeams.people.isSupported());
        }

        initSupportSharing();
    }, [supportSharing]);

    async function handleShare() {
        const message = result;

        if (!message) {
            return;
        }

        await microsoftTeams.app.initialize();
        const context = await microsoftTeams.app.getContext();
        const me = context.user?.loginHint;
        if (!microsoftTeams.people.isSupported()) {
            return;
        }

        const people = await microsoftTeams.people.selectPeople({
            title: 'Select a person to share',
            openOrgWideSearchInChatOrChannel: true,
            singleSelect: true,
        });

        const topicName = 'AI Perfect Assistant';
        const peopleExceptMe = people.filter((p) => p.email !== me);

        const messageWithWarning = `${message} (⚠️ It's an AI generated result, it could be incorrect ⚠️)`;

        if (!microsoftTeams.chat.isSupported()) {
            return;
        }

        const deepLink = `https://teams.microsoft.com/l/chat/0/0?users=${peopleExceptMe
            .map((p) => p.email)
            .join()}&topicName=${encodeURI(topicName)}&message=${encodeURI(
            messageWithWarning,
        )}`;

        if (peopleExceptMe.length === 0) {
            return;
        }

        microsoftTeams.executeDeepLink(deepLink);
        postHog.capture('RESULT.INTERACTION', {
            interactionType: 'share-to-teams',
        });
    }

    if (!supportSharing) {
        return null;
    }

    return (
        <Button
            type='primary'
            icon={<ShareAltOutlined />}
            onClick={handleShare}>
            Share
        </Button>
    );
}

function downloadAsTxt(text: string, filename = 'file.txt') {
    const blob = new Blob([text], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
}

function CopyToClipboardButton({ result }: { result: string }) {
    const messageApi = useMessagesAPI();
    const postHog = usePostHog();
    async function handleCopy() {
        const message = result;
        if (!message) {
            return;
        }
        copy(message, { format: 'text/plain' });

        if (messageApi) {
            messageApi!.info('Copied to clipboard');
        }

        postHog.capture('RESULT.INTERACTION', {
            interactionType: 'copy-to-clipboard',
        });
    }

    const items = [
        {
            key: 'download-txt',
            label: 'Download (.txt)',
            onClick: () =>
                downloadAsTxt(result, `file-${dayjs().toISOString()}.txt`),
        },
        {
            key: 'download-docx',
            label: 'Download (.docx)',
            onClick: () =>
                downloadDocx(result, `file-${dayjs().toISOString()}.docx`),
        },
    ];

    return (
        <Dropdown.Button type='primary' onClick={handleCopy} menu={{ items }}>
            <Copy size='16' className='mr-1' />
            Copy
        </Dropdown.Button>
    );
}

function InsertToWordButton({ result }: { result: string }) {
    const postHog = usePostHog();
    const handleOfficeInsert = async () => {
        if (!result) {
            return;
        }

        const Office = window.Office;
        const options = { coercionType: Office.CoercionType.Text };
        await Office.context.document.setSelectedDataAsync(result, options);
        postHog.capture('RESULT.INTERACTION', {
            interactionType: 'insert-to-word',
        });
    };

    return (
        <Button
            type='primary'
            icon={<SendOutlined />}
            onClick={handleOfficeInsert}>
            Insert
        </Button>
    );
}

function InsertToExcelButton({ result }: { result: string }) {
    const postHog = usePostHog();
    const handleOfficeInsert = async () => {
        if (!result) {
            return;
        }

        await Excel.run(async (context) => {
            const range = context.workbook.getSelectedRange(); // Get the currently selected cell or range
            range.values = [[result]]; // Insert text into the selected cell
            await context.sync();
        });

        postHog.capture('RESULT.INTERACTION', {
            interactionType: 'insert-to-excel',
        });
    };

    return (
        <Button
            type='primary'
            icon={<SendOutlined />}
            onClick={handleOfficeInsert}>
            Insert
        </Button>
    );
}

function InsertToOutlookMessageBodyButton({ result }: { result: string }) {
    const postHog = usePostHog();

    const Office = window.Office;
    if (!Office?.context?.mailbox) {
        return null;
    }

    const handleOfficeInsert = async () => {
        if (!result) {
            return;
        }

        try {
            const Office = window.Office;
            const item = Office?.context?.mailbox?.item;

            if (item.displayReplyForm != undefined) {
                // read mode
                item.displayReplyAllForm({
                    htmlBody: result,
                });

                postHog.capture('RESULT.INTERACTION', {
                    interactionType: 'insert-to-outlook',
                });
            } else {
                // compose mode
                item.body.getTypeAsync((asyncResult: any) => {
                    if (
                        asyncResult.status === Office.AsyncResultStatus.Failed
                    ) {
                        console.log(asyncResult.error.message);
                        return;
                    }

                    // Insert data of the appropriate type into the body.
                    if (asyncResult.value === Office?.CoercionType.Html) {
                        // Insert HTML into the body.
                        item.body.setSelectedDataAsync(
                            result,
                            {
                                coercionType: Office.CoercionType.Html,
                                asyncContext: {
                                    optionalVariable1: 1,
                                    optionalVariable2: 2,
                                },
                            },
                            (asyncResult: any) => {
                                if (
                                    asyncResult.status ===
                                    Office.AsyncResultStatus.Failed
                                ) {
                                    console.log(asyncResult.error.message);
                                    return;
                                }

                                postHog.capture('RESULT.INTERACTION', {
                                    interactionType: 'insert-to-outlook',
                                });
                            },
                        );
                    } else {
                        // Insert plain text into the body.
                        item.body.setSelectedDataAsync(
                            result,
                            {
                                coercionType: Office.CoercionType.Text,
                                asyncContext: {
                                    optionalVariable1: 1,
                                    optionalVariable2: 2,
                                },
                            },
                            (asyncResult: any) => {
                                if (
                                    asyncResult.status ===
                                    Office.AsyncResultStatus.Failed
                                ) {
                                    console.log(asyncResult.error.message);
                                    return;
                                }
                                postHog.capture('RESULT.INTERACTION', {
                                    interactionType: 'insert-to-outlook',
                                });
                            },
                        );
                    }
                });
            }
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <Button type='primary' onClick={handleOfficeInsert}>
            Paste to Outlook
        </Button>
    );
}

export function AiResultForm({
    result,
    handleGoBack,
    isComposeBox,
}: {
    result: string[];
    handleGoBack: () => void;
    isComposeBox: boolean;
}) {
    const { isTeams, isWord, isPowerPoint, isOutlook, isExcel } =
        useApplicationContext()!;

    const [resultIdx, setIdx] = useState(0);
    const displayResult = result[resultIdx];

    return (
        <div className='mx-4 flex flex-col h-full'>
            <div className='flex flex-col flex-grow items-center'>
                <Segmented
                    className='py-4 w-full'
                    value={resultIdx}
                    options={[
                        { value: 0, label: 'Option 1' },
                        { value: 1, label: 'Option 2' },
                        { value: 2, label: 'Option 3' },
                    ]}
                    block
                    onChange={(selectedOption) => setIdx(selectedOption)}
                />
                <Form.Item
                    className='w-full'
                    label='Result generated by AI'
                    tooltip={{
                        title: 'It is an AI generated result so it could be incorrect',
                        icon: <InfoCircleOutlined />,
                    }}>
                    <Input.TextArea
                        style={{ height: '350px', resize: 'none' }}
                        value={displayResult}
                        placeholder='AI Generated result will appear here'
                    />
                </Form.Item>
            </div>
            <div className='flex items-end justify-between sticky bottom-0 backdrop-blur-sm w-full py-4 border-t'>
                <Space wrap={true}>
                    <Button onClick={handleGoBack}>
                        &larr;
                        <span className='w-0 opacity-0 sm:w-auto sm:opacity-100'>
                            &nbsp;Go Back
                        </span>
                    </Button>
                </Space>
                <Space wrap={false} direction={'horizontal'}>
                    <CopyToClipboardButton result={displayResult} />
                    {isTeams && isComposeBox && (
                        <SubmitToComposeBoxButton result={displayResult} />
                    )}
                    {isTeams && !isComposeBox && (
                        <ShareToTeamsButton result={displayResult} />
                    )}
                    {(isWord || isPowerPoint) && (
                        <InsertToWordButton result={displayResult} />
                    )}
                    {isExcel && <InsertToExcelButton result={displayResult} />}
                    {isOutlook && (
                        <InsertToOutlookMessageBodyButton
                            result={displayResult}
                        />
                    )}
                </Space>
            </div>
        </div>
    );
}

export function LoadingPlaceholder() {
    return (
        <div className='flex h-full w-full items-center justify-center'>
            <div className='max-w-[300px] m-[-50px]'>
                <Lottie animationData={LoadingAnimation} />
            </div>
        </div>
    );
}

export function AiPromptModalBody({
    currentState,
    setCurrentState,
    aiTemplate,
    autoSubmit = false,
    isFreeMode = false,
    handleShowIntegrationsClick,
}: {
    currentState?: string;
    setCurrentState?: (state: string) => void;
    aiTemplate: PromptDefinition;
    isFreeMode: boolean;
    autoSubmit?: boolean;
    handleShowIntegrationsClick: () => void;
}) {
    const autoSubmitRef = useRef(autoSubmit || false);
    const { id } = aiTemplate;
    const [form] = Form.useForm();
    const USER_INPUT_STATE = 'USER_INPUT_STATE';
    const AI_RESULT_STATE = 'AI_RESULT_STATE';
    const FETCHING = 'FETCHING';

    const [innerState, setInnerFormState] = useState(USER_INPUT_STATE);
    let formState: string, setFormState: (state: string) => void;

    if (currentState && setCurrentState) {
        formState = currentState;
        setFormState = setCurrentState;
    } else {
        formState = innerState;
        setFormState = setInnerFormState;
    }

    const [params] = useSearchParams();

    const isComposeBox = params.get('isComposeBox') ? true : false;
    const applicationContext = useApplicationContext()!;
    const account = applicationContext?.account;
    const { tokens, decreaseTokens } = useDisplayTokens();
    const { openDialog: openSignInDialog } = useSignInModal();
    const { openDialog: openSubscriptionPromotionDialog } =
        useSubscriptionPromotionModal();

    const [initialFormValues, setInitialFormValues] =
        useAtom(defaultFormValues);

    const removeTextDraft = useCallback(() => {
        setInitialFormValues({ ...initialFormValues, text: null });
    }, [initialFormValues, setInitialFormValues]);

    const initialValues = {
        ...initialFormValues,
        text: aiTemplate.userPrompt || initialFormValues.text,
    };

    const debouncedUpdateInitialFormFields = useDebouncedCallback(
        // function
        () => {
            const values = form.getFieldsValue();
            setInitialFormValues({ ...initialFormValues, ...values });
        },
        // delay in ms
        300,
    );

    useEffect(() => {
        if (aiTemplate.userPrompt) {
            form.setFieldValue('text', aiTemplate.userPrompt);
        }
    }, [aiTemplate.userPrompt, form]);

    useEffect(() => {
        if (!autoSubmitRef.current) {
            return;
        }

        form.submit();
        autoSubmitRef.current = false;
    }, [form, autoSubmitRef]);

    const postHog = usePostHog();
    const handleFormSubmit = useCallback(
        async (values: any) => {
            if (!isFreeMode && account && !account.isUnlimited && tokens <= 0) {
                openSubscriptionPromotionDialog();
                return;
            }

            setFormState(FETCHING);
            const payload = JSON.stringify({
                ...values,
                id: id,
                imageUrl: values?.imgUrl,
            });

            const res = await fetch(
                isFreeMode || !account ? '/ai/free' : '/ai',
                {
                    method: 'POST',
                    body: payload,
                },
            );
            const json = await res.json();

            form.setFieldValue('result', json.responses);

            if (
                json.responses[0].includes(
                    'Sign up to continue using Perfect Assistant!',
                )
            ) {
                openSignInDialog();
            }

            setFormState(AI_RESULT_STATE);
            decreaseTokens();
            removeTextDraft();
        },
        [
            isFreeMode,
            account,
            tokens,
            setFormState,
            id,
            form,
            decreaseTokens,
            postHog,
            aiTemplate.id,
            aiTemplate.title,
            removeTextDraft,
            openSignInDialog,
            openSubscriptionPromotionDialog,
        ],
    );

    function handleGoBack() {
        setFormState(USER_INPUT_STATE);
    }

    function renderCurrentState() {
        switch (formState) {
            case USER_INPUT_STATE:
                return (
                    <UserInputForm
                        aiTemplate={aiTemplate}
                        form={form}
                        isFreeMode={isFreeMode || !account}
                        handleShowIntegrationsClick={
                            handleShowIntegrationsClick
                        }
                    />
                );
            case FETCHING:
                return <LoadingPlaceholder />;
            case AI_RESULT_STATE:
                return (
                    <AiResultForm
                        result={form.getFieldValue('result')}
                        isComposeBox={isComposeBox}
                        handleGoBack={handleGoBack}
                    />
                );
            default:
                throw new Error(`Unknown state ${formState}`);
        }
    }

    return (
        <Form
            form={form}
            name='basic'
            layout='vertical'
            className='flex flex-col'
            style={{ width: '100%', height: '100%' }}
            initialValues={initialValues}
            autoComplete='off'
            onFieldsChange={(fields, allFields) => {
                if (!fields.some((f) => f.touched)) {
                    return;
                }
                debouncedUpdateInitialFormFields();
            }}
            onFinish={handleFormSubmit}>
            {renderCurrentState()}
        </Form>
    );
}

const aiPromptDialogState = atom(false);
const aiPromptState = atom<PromptDefinition | null>(null);

export function useAiPromptForm() {
    const [prompt, setPrompt] = useAtom(aiPromptState);
    const {
        openDialog: openSuperDialog,
        closeDialog,
        isOpened,
    } = useDialog(aiPromptDialogState);

    const openDialog = useCallback(
        (prompt: PromptDefinition) => {
            setPrompt(prompt);
            openSuperDialog();
        },
        [openSuperDialog, setPrompt],
    );

    return { openDialog, closeDialog, isOpened, prompt };
}

export default function AiPromptForm() {
    const { isOpened, closeDialog, prompt: aiTemplate } = useAiPromptForm();

    const title = (
        <div>
            {aiTemplate?.emoji}&nbsp;{aiTemplate?.title}
        </div>
    );

    return (
        <Drawer
            title={title}
            open={isOpened}
            destroyOnClose={true}
            onClose={closeDialog}
            size='large'
            maskClosable={false}
            classNames={{ body: '!p-0' }}
            footer={null}>
            <div className='flex h-full w-full flex-col'>
                <AiPromptModalBody
                    aiTemplate={aiTemplate!}
                    isFreeMode={false}
                />
            </div>
        </Drawer>
    );
}
